import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { userService } from '@/services/userService'
import {
  validateAccount,
  validatePassword,
  validateDisplayName,
  validatePasswordConfirmation
} from '@/utils/validation'

// Type definitions
type UserRole = 'owner' | 'admin' | 'auditor' | 'security' | 'user'

interface FormData {
  account: string
  name: string
  platform_role: UserRole
  password: string
  confirmPassword: string
}

interface FormErrors {
  account: string
  name: string
  password: string
  confirmPassword: string
}

interface AddUserModalProps {
  currentUserRole: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  onSuccess?: () => void
}

export interface AddUserModalRef {
  open: () => void
  close: () => void
}

// Initial state constants
const INITIAL_FORM_DATA: FormData = {
  account: '',
  name: '',
  platform_role: 'user',
  password: '',
  confirmPassword: '',
}

const INITIAL_FORM_ERRORS: FormErrors = {
  account: '',
  name: '',
  password: '',
  confirmPassword: '',
}

const AddUserModal = forwardRef<AddUserModalRef, AddUserModalProps>(({ currentUserRole, onSuccess }, ref) => {
  const { t } = useTranslation('users')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [actionType, setActionType] = useState<'direct' | 'request'>('direct')

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)
  const [formErrors, setFormErrors] = useState<FormErrors>(INITIAL_FORM_ERRORS)

  const isOwner = currentUserRole === 'owner'

  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true)
      setError('')
      setSuccess(false)
      setActionType(isOwner ? 'direct' : 'request')
      setFormData({ ...INITIAL_FORM_DATA })
      setFormErrors({ ...INITIAL_FORM_ERRORS })
    },
    close: () => {
      setIsOpen(false)
      setSuccess(false)
      setActionType('direct')
    },
  }), [isOwner])

  const validateForm = (): boolean => {
    const errors: FormErrors = { ...INITIAL_FORM_ERRORS }

    // Validate account
    const accountValidation = validateAccount(formData.account)
    if (!accountValidation.isValid && accountValidation.error) {
      errors.account = t(accountValidation.error, accountValidation.error)
    }

    // Validate display name
    const nameValidation = validateDisplayName(formData.name)
    if (!nameValidation.isValid && nameValidation.error) {
      errors.name = t(nameValidation.error, nameValidation.error)
    }

    // Validate password
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid && passwordValidation.error) {
      errors.password = t(passwordValidation.error, passwordValidation.error)
    }

    // Validate password confirmation
    const confirmPasswordValidation = validatePasswordConfirmation(
      formData.password,
      formData.confirmPassword
    )
    if (!confirmPasswordValidation.isValid && confirmPasswordValidation.error) {
      errors.confirmPassword = t(confirmPasswordValidation.error, confirmPasswordValidation.error)
    }

    setFormErrors(errors)
    return !Object.values(errors).some(error => error !== '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      setError('')

      // Sanitize inputs before sending
      const userData = {
        account: formData.account.trim(),
        name: formData.name.trim(),
        password: formData.password,
        role: formData.platform_role,
      }

      if (actionType === 'direct') {
        // Owner直接創建
        await userService.createUser(userData)
      } else {
        // 提交請求 (Admin或Owner選擇request方式)
        await userService.requestUserCreate(userData)
      }

      // Show success message instead of closing immediately
      setSuccess(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    const wasSuccess = success
    setIsOpen(false)
    setFormData({ ...INITIAL_FORM_DATA })
    setFormErrors({ ...INITIAL_FORM_ERRORS })
    setError('')
    setSuccess(false)
    setActionType('direct')

    // Only call onSuccess if the operation was successful
    if (wasSuccess) {
      onSuccess?.()
    }
  }

  const handleChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (field in formErrors) {
      setFormErrors(prev => ({ ...prev, [field as keyof FormErrors]: '' }))
    }
  }

  // Real-time validation on blur
  const handleBlur = (field: keyof FormErrors) => {
    let validationResult

    switch (field) {
      case 'account':
        validationResult = validateAccount(formData.account)
        break
      case 'name':
        validationResult = validateDisplayName(formData.name)
        break
      case 'password':
        validationResult = validatePassword(formData.password)
        break
      case 'confirmPassword':
        validationResult = validatePasswordConfirmation(formData.password, formData.confirmPassword)
        break
      default:
        return
    }

    if (!validationResult.isValid && validationResult.error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: t(validationResult.error!, validationResult.error!)
      }))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 bg-linear-to-r from-primary/5 to-transparent">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">{t('addModal.title', 'Add User')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('addModal.subtitle', 'Create a new local user account')}</p>
            </div>
            <button
              onClick={handleClose}
              className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
              disabled={loading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('addModal.successDirect', 'User created successfully!')
                        : t('addModal.successRequest', 'User creation request submitted successfully!')
                      }
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('addModal.successDirectDesc', 'The user account has been created immediately.')
                        : t('addModal.successRequestDesc', 'The request is pending owner approval.')
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 flex-1">{error}</p>
              </div>
            )}

            {/* Account and Name Fields - Two Column Layout */}
            {!success && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Account Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('addModal.account', 'Account Name')}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  value={formData.account}
                  onChange={(e) => handleChange('account', e.target.value)}
                  onBlur={() => handleBlur('account')}
                  placeholder={t('addModal.accountPlaceholder', 'Please enter account name')}
                  className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    formErrors.account
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-primary/30'
                  }`}
                  disabled={loading}
                />
                {formErrors.account && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formErrors.account}
                  </p>
                )}
              </div>

              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('addModal.name', 'Display Name')}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder={t('addModal.namePlaceholder', 'Please enter display name')}
                  className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    formErrors.name
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-primary/30'
                  }`}
                  disabled={loading}
                />
                {formErrors.name && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formErrors.name}
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Role Field - Full Width */}
            {!success && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('addModal.role', 'Role')}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <select
                value={formData.platform_role}
                onChange={(e) => handleChange('platform_role', e.target.value as UserRole)}
                className="w-full min-h-[44px] h-[44px] px-3.5 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white transition-all appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.25rem 1.25rem',
                  paddingRight: '2.5rem'
                }}
                disabled={loading}
              >
                <option value="user">{t('roles.user', 'User')}</option>
                <option value="auditor">{t('roles.auditor', 'Auditor')}</option>
                <option value="security">{t('roles.security', 'Security')}</option>
                <option value="admin">{t('roles.admin', 'Admin')}</option>
                <option value="owner">{t('roles.owner', 'Owner')}</option>
              </select>
            </div>
            )}

            {/* Password Fields - Two Column Layout */}
            {!success && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('addModal.password', 'Password')}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  onBlur={() => handleBlur('password')}
                  placeholder={t('addModal.passwordPlaceholder', 'Please enter password')}
                  className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    formErrors.password
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-primary/30'
                  }`}
                  disabled={loading}
                />
                {formErrors.password && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formErrors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('addModal.confirmPassword', 'Confirm Password')}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  onBlur={() => handleBlur('confirmPassword')}
                  placeholder={t('addModal.confirmPasswordPlaceholder', 'Please confirm password')}
                  className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    formErrors.confirmPassword
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-primary/30'
                  }`}
                  disabled={loading}
                />
                {formErrors.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Action Type Selection (Only for Owner) */}
            {!success && isOwner && (
              <div className="pt-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('addModal.actionType', 'Action Type')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('addModal.directCreate', 'Direct Create')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('addModal.directCreateDesc', 'Immediately create the user account')}
                      </p>
                    </div>
                  </label>
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('addModal.submitRequest', 'Submit Request')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('addModal.submitRequestDesc', 'Create a user creation request')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin Info Notice */}
            {!success && !isOwner && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('addModal.adminNotice', 'As an admin, you can submit a user creation request. The request will need to be approved by an owner.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Form Note */}
            {!success && (
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-3 flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-blue-700">
                {t('addModal.formNote', 'Please fill in complete information')}
              </p>
            </div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-end gap-3">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                {t('addModal.close', 'Close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all font-medium"
                  disabled={loading}
                >
                  {t('addModal.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow"
                  disabled={loading}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('addModal.processing', 'Processing')
                      : actionType === 'direct'
                        ? t('addModal.create', 'Create User')
                        : t('addModal.submitRequest', 'Submit Request')
                    }
                  </span>
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
})

AddUserModal.displayName = 'AddUserModal'

export default AddUserModal
